import { audioRecordingModule } from "@/modules/audio-recording";
import { logger } from "@/modules/logger";
import {
  isWebAudioArtifactMissingOrEmpty,
  WebAudioArtifactRef,
  WebAudioArtifactService,
} from "@/modules/audio-recording/web-artifacts";
import { uuidModule } from "@/modules/uuid";
import { AudioRecordingRepository } from "@/domains/dexie-db/repositories/audio-recording/audio-recording";
import { LocalAudioRecordingTranscriptTurnsRepository } from "@/domains/dexie-db/repositories/audio-recording/local-audio-recording-transcript-turns";
import { buildAudioRecordingArtifactDiagnostics } from "@/store/audio/audio-recording-artifact-diagnostics";
import {
  AudioRecordingRef,
  AudioUploadPayloadProvider,
  PreparedAudioUploadResult,
} from "@/store/audio/uploading/audio-uploading.types";
import { buildAudioUploadMergeIntentPayload } from "@/store/audio/audio-recording-merge-intent";

type WebAudioUploadPayloadProviderConfig = {
  recordings: AudioRecordingRepository;
  transcripts: LocalAudioRecordingTranscriptTurnsRepository;
  artifacts: WebAudioArtifactService;
};

const WEB_AUDIO_ARTIFACT_UPLOAD_READINESS_MAX_WAIT_MS = 1_000;
const WEB_AUDIO_ARTIFACT_UPLOAD_READINESS_POLL_INTERVAL_MS = 25;

export class WebAudioUploadPayloadProvider implements AudioUploadPayloadProvider<WebAudioArtifactRef> {
  private readonly recordings: AudioRecordingRepository;
  private readonly transcripts: LocalAudioRecordingTranscriptTurnsRepository;
  private readonly artifacts: WebAudioArtifactService;

  constructor({ recordings, transcripts, artifacts }: WebAudioUploadPayloadProviderConfig) {
    this.recordings = recordings;
    this.transcripts = transcripts;
    this.artifacts = artifacts;
  }

  async prepareUploadPayload({
    audioRecordingId,
  }: AudioRecordingRef): Promise<PreparedAudioUploadResult<WebAudioArtifactRef>> {
    logger.info({
      message: "[WebAudioUploadPayloadProvider] prepare upload payload start",
      info: { audioRecordingId },
    });

    const recording = await this.recordings.getById({ id: audioRecordingId });
    logger.info({
      message: "[WebAudioUploadPayloadProvider] recording lookup complete",
      info: { audioRecordingId, found: Boolean(recording) },
    });

    if (!recording) {
      logger.error({
        message: "[WebAudioUploadPayloadProvider] missing audio recording",
        info: { audioRecordingId },
      });

      return {
        success: false,
        error: "Recording not found",
        shouldPersistError: false,
      };
    }

    const artifactRef = await this.waitForReadableArtifactRef({
      audioRecordingId,
    });
    logger.info({
      message: "[WebAudioUploadPayloadProvider] artifact lookup complete",
      info: {
        audioRecordingId,
        hasArtifact: artifactRef !== null,
        artifactSizeBytes: artifactRef?.sizeBytes ?? null,
      },
    });

    if (isWebAudioArtifactMissingOrEmpty({ artifactRef })) {
      const diagnostics = buildAudioRecordingArtifactDiagnostics({
        diagnosticsSource: "web-upload-payload-provider",
        audioRecordingId,
        modelData: recording.data,
        artifactAvailability: artifactRef === null ? "missing" : "empty",
        artifactSizeBytes: artifactRef?.sizeBytes ?? null,
        artifactDurationMs: artifactRef?.durationMs ?? null,
        artifactPartCount: artifactRef?.chunkCount ?? null,
        artifactMimeType: artifactRef?.mimeType ?? null,
        hasUploadableArtifact: artifactRef !== null && artifactRef.sizeBytes > 0,
        extraInfo: {
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "unknown",
        },
      });

      if (diagnostics.isTooShort === true) {
        logger.info({
          message: "[WebAudioUploadPayloadProvider] recording too short - finalized artifact empty",
          info: diagnostics,
        });
      } else {
        logger.error({
          message: "[WebAudioUploadPayloadProvider] finalized audio artifact is unavailable",
          info: diagnostics,
        });
      }

      return {
        success: false,
        error: diagnostics.expectedUserErrorMessage,
      };
    }

    if (!artifactRef) {
      return {
        success: false,
        error: "Recording audio is unavailable",
      };
    }

    const transcriptRecords = await this.transcripts.getAllByRecordingId({
      associatedAudioRecordingId: audioRecordingId,
    });
    const { transcriptArray, transcriptText } =
      audioRecordingModule.buildTranscriptPayload(transcriptRecords);
    const capturedAudioId = uuidModule.resolveAudioRecordingCapturedAudioUuid({
      associatedAudioRecordingId: audioRecordingId,
    });
    const associatedNoteId = recording.data.associated_note_id ?? null;
    const associatedSessionId = associatedNoteId
      ? uuidModule.resolveCapturedAudioSessionSyncModelUuid({ capturedAudioId })
      : null;
    const audioRecordingMergeIntent = buildAudioUploadMergeIntentPayload({
      audioRecordingId,
      modelData: recording.data,
    });

    return {
      success: true,
      payload: {
        source: artifactRef,
        uploadMeta: {
          capturedAudioId,
          associatedAudioRecordingId: audioRecordingId,
          associatedNoteId,
          associatedSessionId,
          preferredMimeType: artifactRef.mimeType || undefined,
          transcriptArray,
          transcriptText,
          audioRecordingMergeIntent,
        },
      },
    };
  }

  /**
   * IndexedDB can transiently return a pre-finalize snapshot immediately after recording
   * completion. Retry briefly before classifying the artifact as missing.
   */
  private async waitForReadableArtifactRef({
    audioRecordingId,
  }: AudioRecordingRef): Promise<WebAudioArtifactRef | null> {
    const deadlineMs = Date.now() + WEB_AUDIO_ARTIFACT_UPLOAD_READINESS_MAX_WAIT_MS;
    let attemptCount = 0;

    while (true) {
      attemptCount += 1;
      const artifactRef = await this.artifacts.getAudioArtifactRef({ audioRecordingId });

      if (artifactRef && artifactRef.sizeBytes > 0) {
        if (attemptCount > 1) {
          logger.info({
            message: "[WebAudioUploadPayloadProvider] artifact became readable after retry",
            info: {
              audioRecordingId,
              attemptCount,
              artifactSizeBytes: artifactRef.sizeBytes,
              waitMs: WEB_AUDIO_ARTIFACT_UPLOAD_READINESS_POLL_INTERVAL_MS * (attemptCount - 1),
            },
          });
        }

        return artifactRef;
      }

      if (Date.now() >= deadlineMs) {
        return artifactRef;
      }

      await new Promise(resolve =>
        setTimeout(resolve, WEB_AUDIO_ARTIFACT_UPLOAD_READINESS_POLL_INTERVAL_MS)
      );
    }
  }
}
